import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Collapse,
  Divider,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ContactPI from './ContactPI ';

/**
 * EnhancedTrialCard - Displays trial with locations and contact information
 * NOW WITH: Book Travel integration!
 */
const EnhancedTrialCard = ({ trial, matchScore, patientProfile }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);
  const navigate = useNavigate();

  // Safety check
  if (!trial) {
    return null;
  }

  // Filter to only US locations
  const usLocations = trial.trialLocations?.filter(
    loc => loc.country === 'United States' || 
           loc.country === 'USA' || 
           loc.country === 'US'
  ) || [];

  /**
   * Handle Book Travel - Navigate to travel page with trial context
   * @param {object} location - Specific location to book travel for
   */
  const handleBookTravel = (location = null) => {
    console.log('🚀 Book Travel clicked!');
    console.log('📍 Location:', location);
    console.log('🏥 Trial:', trial);
    
    // Build trial context with specific location (if provided)
    const trialContext = {
      trialId: trial.nctId,
      trialName: trial.title,
      destination: location?.city || null,
      destinationState: location?.state || null,
      facilityName: location?.facilityName || null,
      sponsor: trial.sponsor,
      phase: trial.phase,
    };

    console.log('💾 Storing trial context:', trialContext);

    // Store in sessionStorage for travel page to access
    sessionStorage.setItem('ac_trial_context', JSON.stringify(trialContext));
    
    // Verify it was stored
    const verification = sessionStorage.getItem('ac_trial_context');
    console.log('✅ Verification - stored value:', verification);

    // Navigate to travel search page
    console.log('🧭 Navigating to /travel/search');
    navigate('/travel/search');
  };

  return (
    <Card 
      elevation={3} 
      sx={{ 
        mb: 2,
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out',
        }
      }}
    >
      <CardContent>
        {/* Header with Match Score */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            {trial.title}
          </Typography>
          {matchScore && (
            <Chip 
              label={`${matchScore}% Match`} 
              color="primary" 
              sx={{ ml: 2, fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Trial Metadata */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            component="a"
            href={`https://clinicaltrials.gov/study/${trial.nctId}`}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            label={`NCT ID: ${trial.nctId}`}
            size="small"
            variant="outlined"
          />
          <Chip 
            label={trial.status || 'Status Unknown'} 
            size="small" 
            color={trial.status === 'RECRUITING' ? 'success' : 'default'}
          />
          {trial.phase && (
            <Chip 
              label={trial.phase} 
              size="small" 
              variant="outlined"
            />
          )}
          {usLocations.length > 0 && (
            <Chip 
              icon={<LocationOnIcon />}
              label={`${usLocations.length} US Location${usLocations.length > 1 ? 's' : ''}`} 
              size="small" 
              color="info"
            />
          )}
        </Box>

        {/* Brief Summary */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {trial.briefSummary || trial.description || 'No description available'}
        </Typography>

        {/* 🆕 VIEW DETAILS BUTTON */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none' }}
          >
            {expanded ? 'Hide Details' : 'View Details'}
          </Button>
        </Box>

        {/* Show generic Book Travel if no locations */}
        {usLocations.length === 0 && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<FlightTakeoffIcon />}
              onClick={() => handleBookTravel(null)}
              sx={{
                background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)',
                },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Book Travel for This Trial
            </Button>
          </Box>
        )}

        {/* Locations Section */}
        {usLocations.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                📍 Trial Locations ({usLocations.length})
              </Typography>
              {usLocations.length > 3 && (
                <Button 
                  size="small" 
                  onClick={() => setShowAllLocations(!showAllLocations)}
                  sx={{ textTransform: 'none' }}
                >
                  {showAllLocations ? 'Show Less' : `Show All ${usLocations.length}`}
                </Button>
              )}
            </Box>

            {/* Display locations */}
            {(showAllLocations ? usLocations : usLocations.slice(0, 3)).map((location, idx) => (
              <LocationCard 
                key={idx} 
                location={location} 
                trial={trial}
                patientProfile={patientProfile}
                onBookTravel={handleBookTravel}
              />
            ))}
          </Box>
        )}

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />
          
          {/* Eligibility Criteria */}
          {trial.eligibilityCriteria && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Eligibility Criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {trial.eligibilityCriteria}
              </Typography>
            </Box>
          )}

          {/* Detailed Description */}
          {trial.detailedDescription && trial.detailedDescription !== trial.briefSummary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Detailed Description
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {trial.detailedDescription}
              </Typography>
            </Box>
          )}

          {/* Sponsor */}
          {trial.sponsor && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Sponsor
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {trial.sponsor}
              </Typography>
            </Box>
          )}

          {/* Collaborators */}
          {trial.collaborators && trial.collaborators.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Collaborators
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {trial.collaborators.join(', ')}
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

/**
 * LocationCard - Individual location with contact info
 */
const LocationCard = ({ location, trial, patientProfile, onBookTravel }) => {
  return (
    <Card variant="outlined" sx={{ mb: 1, bgcolor: 'background.default' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Facility Name */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <LocalHospitalIcon color="primary" sx={{ mt: 0.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {location.facilityName || 'Facility Name Not Available'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[location.city, location.state, location.zipCode].filter(Boolean).join(', ')}
            </Typography>
          </Box>
          {location.status && (
            <Chip 
              label={location.status} 
              size="small"
              color={location.status.toLowerCase().includes('recruiting') ? 'success' : 'default'}
            />
          )}
        </Box>

        {/* Book Travel Button for this location */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<FlightTakeoffIcon />}
            onClick={() => onBookTravel(location)}
            sx={{
              background: 'linear-gradient(135deg, #F97316 0%, #EA580C 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)',
              },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Book Travel to {location.city}
          </Button>
        </Box>

        {/* Contact Information */}
        {location.primaryContact && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, backgroundColor: '#f0f4ff' }}>
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
              👨‍⚕️ Principal Investigator
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {location.primaryContact.contactName}
            </Typography>
            <ContactPI 
              trial={trial}
              contact={location.primaryContact}
              location={location}
              patientProfile={patientProfile}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default EnhancedTrialCard;